import { useEffect, useRef } from "react";
import { FieldValues, UseFormClearErrors } from "react-hook-form";

import { useSyncedClearErrors } from "@RequestFormV2/hooks/useSyncedClearErrors";

/**
 * Runs a relational (cross-field) validator onChange so conflicts surface inline
 * as the user edits — without waiting for Review.
 *
 * - `run(clear)` should call the existing pure validator with the form's `setError`
 *   and the `clear` passed in. Those validators already set *and* clear with the
 *   correct i18n message, so this hook just decides *when* to run them.
 * - Sets use plain `setError` (inline badge only — the accordion header is not
 *   lit before Review).
 * - Clears route through `clearFieldError` after Review so the accordion badge
 *   stays accurate when a fix removes the section's last error.
 * - The first render is skipped so a freshly-loaded draft with a pre-existing
 *   conflict stays quiet until the user actually changes a relevant field.
 */
export function useRelationalOnChangeValidation(
  run: (clear: UseFormClearErrors<FieldValues>) => void,
  deps: unknown[],
  enabled = true,
) {
  const syncedClear = useSyncedClearErrors();
  const isFirst = useRef(true);

  useEffect(() => {
    if (!enabled) return;
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    run(syncedClear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, syncedClear, ...deps]);
}
